const FORM_SUBMISSIONS_API_URL = '/api/form-submissions';
const LOCAL_STORAGE_KEY = 'site-form-submissions';

const extractErrorMessage = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => ({}));
  return payload?.message || fallbackMessage;
};

const getLocalSubmissions = () => {
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

const setLocalSubmissions = (items) => {
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(Array.isArray(items) ? items : []));
  } catch {
    // ignore localStorage failures
  }
};

const prependLocalSubmission = (item) => {
  if (!item || !item.id) return;
  const current = getLocalSubmissions();
  const next = [item, ...current.filter((row) => row?.id !== item.id)].slice(0, 1000);
  setLocalSubmissions(next);
};

const createLocalSubmission = (payload) => ({
  id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: String(payload?.name || payload?.username || '').trim(),
  email: String(payload?.email || '').trim(),
  phone: String(payload?.phone || '').trim(),
  message: String(payload?.message || '').trim(),
  source: String(payload?.source || 'unknown').trim(),
  page: String(payload?.page || '').trim(),
  createdAt: new Date().toISOString(),
});

export const submitFormSubmission = async (payload) => {
  try {
    const response = await fetch(FORM_SUBMISSIONS_API_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });

    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Form kaydi basarisiz.'));
    }

    const data = await response.json().catch(() => ({}));
    if (data?.item?.id) {
      prependLocalSubmission(data.item);
      return data.item;
    }
  } catch {
    // fall through to local fallback
  }

  const localItem = createLocalSubmission(payload);
  prependLocalSubmission(localItem);
  return localItem;
};

export const listFormSubmissions = async () => {
  const localItems = getLocalSubmissions();

  try {
    const response = await fetch(FORM_SUBMISSIONS_API_URL, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Form kayitlari alinamadi.'));
    }

    const data = await response.json().catch(() => ({}));
    const apiItems = Array.isArray(data?.items) ? data.items : [];

    const mergedMap = new Map();
    apiItems.forEach((item) => {
      if (item?.id) mergedMap.set(item.id, item);
    });
    localItems.forEach((item) => {
      if (item?.id && !mergedMap.has(item.id)) mergedMap.set(item.id, item);
    });

    const merged = Array.from(mergedMap.values()).sort((a, b) => String(b?.createdAt || '').localeCompare(String(a?.createdAt || '')));
    setLocalSubmissions(merged);
    return merged;
  } catch {
    return localItems;
  }
};

export const deleteFormSubmission = async (id) => {
  const targetId = String(id || '').trim();
  if (!targetId) return;

  const localItems = getLocalSubmissions().filter((item) => item?.id !== targetId);
  setLocalSubmissions(localItems);

  try {
    const target = `${FORM_SUBMISSIONS_API_URL}?id=${encodeURIComponent(targetId)}`;
    const response = await fetch(target, { method: 'DELETE' });

    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Form kaydi silinemedi.'));
    }
  } catch {
    // local state already updated
  }
};

export const clearFormSubmissions = async () => {
  setLocalSubmissions([]);

  try {
    const response = await fetch(FORM_SUBMISSIONS_API_URL, { method: 'DELETE' });

    if (!response.ok) {
      throw new Error(await extractErrorMessage(response, 'Form kayitlari temizlenemedi.'));
    }
  } catch {
    // local state already cleared
  }
};
